// Multi-chat store for ToneGuard. Single-device, localStorage-only.
//
// Schema v2:
//   {
//     version: 2,
//     chats: [
//       {
//         id, name, createdAt, lastActivityAt,
//         messages: [...]              // same shape as the old v1 messages
//         preferredStyle: string|null,
//         sessionId: string,            // own LLM session per chat
//         // Relationship register fields (background data layer — set by
//         // detectRegisterFromLabel + the Step 4 register question UI):
//         relationship_register: string|null,  // 'work'|'friend'|'family'|'close'|null
//         register_confirmed: boolean,         // true once the register is
//                                              //   decided (silently via
//                                              //   keyword, manually via
//                                              //   the register question,
//                                              //   or skipped)
//         is_first_name: boolean,              // true only for Priority 3
//       }
//     ],
//     activeChatId: string|null,
//     lastIntent: string|null,          // user-level (not per-chat)
//     soundEnabled: boolean,
//   }
//
// On first read, if `toneguard:v1` exists with messages, we migrate it into
// a single chat called "Earlier drafts" so existing users keep their history.

const STORAGE_KEY = "toneguard:v2";
const LEGACY_KEY = "toneguard:v1";

const makeId = () => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const makeSession = () => `tg-${makeId().slice(0, 12)}`;

const blankStore = () => ({
    version: 2,
    chats: [],
    activeChatId: null,
    lastIntent: null,
    soundEnabled: false,
    userProfile: { name: "Friend", photo: null },
});

const isStoreShape = (x) =>
    x && typeof x === "object" && x.version === 2 && Array.isArray(x.chats);

/**
 * One-shot migration from v1 → v2. If `toneguard:v1` carries messages,
 * wrap them into a single chat named "Earlier drafts" and write v2.
 * Keeps the legacy key around for safety; nothing reads it after migration.
 */
function migrateFromLegacy() {
    try {
        const raw = window.localStorage.getItem(LEGACY_KEY);
        if (!raw) return null;
        const v1 = JSON.parse(raw);
        if (!v1 || !Array.isArray(v1.messages) || v1.messages.length === 0) {
            return null;
        }
        const last = v1.messages[v1.messages.length - 1];
        const chat = {
            id: makeId(),
            name: "Earlier drafts",
            createdAt: new Date().toISOString(),
            lastActivityAt:
                last?.createdAt || new Date().toISOString(),
            messages: v1.messages,
            preferredStyle: v1.preferredStyle || null,
            sessionId: v1.sessionId || makeSession(),
        };
        const store = {
            version: 2,
            chats: [chat],
            activeChatId: chat.id,
            lastIntent: v1.lastIntent || null,
            soundEnabled: !!v1.soundEnabled,
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
        return store;
    } catch {
        return null;
    }
}

export function loadStore() {
    if (typeof window === "undefined") return blankStore();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (isStoreShape(parsed)) return parsed;
        }
        const migrated = migrateFromLegacy();
        if (migrated) return migrated;
    } catch {
        /* fall through */
    }
    return blankStore();
}

export function saveStore(store) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        /* quota / privacy mode — ignore */
    }
}

// ---------- CRUD ----------

export function createChat(store, name = "New chat", opts = {}) {
    const {
        register = null,
        registerConfirmed = false,
        isFirstName = false,
        phone_number = null,
        email_address = null,
    } = opts;
    const chat = {
        id: makeId(),
        name: (name || "New chat").trim() || "New chat",
        createdAt: new Date().toISOString(),
        lastActivityAt: new Date().toISOString(),
        messages: [],
        preferredStyle: null,
        sessionId: makeSession(),
        // Relationship register fields — additive, NEVER drives any UI
        // outside the chat-creation register question + Layer 2/3/4
        // intelligence. Existing chats created before this commit are
        // grandfathered via the lazy guard in getActiveChat (treated as
        // already-confirmed so no register question pops on legacy data).
        relationship_register: register || null,
        register_confirmed: !!registerConfirmed,
        is_first_name: !!isFirstName,
        // Optional contact details — both also stored server-side in the
        // `chat_contacts` Mongo collection so they survive cross-device.
        // The localStorage copy is the fast read for the SendPanel.
        phone_number: phone_number || null,
        email_address: email_address || null,
    };
    return {
        ...store,
        chats: [chat, ...store.chats],
        activeChatId: chat.id,
    };
}

export function renameChat(store, id, name) {
    const clean = (name || "").trim() || "New chat";
    return {
        ...store,
        chats: store.chats.map((c) =>
            c.id === id ? { ...c, name: clean } : c,
        ),
    };
}

export function deleteChat(store, id) {
    const remaining = store.chats.filter((c) => c.id !== id);
    const newActive =
        store.activeChatId === id
            ? remaining[0]?.id || null
            : store.activeChatId;
    return { ...store, chats: remaining, activeChatId: newActive };
}

export function setActiveChat(store, id) {
    return { ...store, activeChatId: id };
}

/**
 * Replace the messages array of a chat AND bump lastActivityAt to the
 * most recent message timestamp (or now if the chat is being cleared).
 */
export function updateChatMessages(store, id, messages, extras = {}) {
    return {
        ...store,
        chats: store.chats.map((c) => {
            if (c.id !== id) return c;
            const last = messages?.[messages.length - 1];
            return {
                ...c,
                messages,
                lastActivityAt:
                    last?.createdAt || c.lastActivityAt || new Date().toISOString(),
                ...extras,
            };
        }),
    };
}

export function updateChatField(store, id, patch) {
    return {
        ...store,
        chats: store.chats.map((c) =>
            c.id === id ? { ...c, ...patch } : c,
        ),
    };
}

// ---------- Helpers ----------

export function getActiveChat(store) {
    if (!store?.activeChatId) return null;
    return store.chats.find((c) => c.id === store.activeChatId) || null;
}

const PREVIEW_MAX = 70;

export function chatPreview(chat) {
    const msgs = chat?.messages || [];
    if (msgs.length === 0) return "No messages yet";
    const last = msgs[msgs.length - 1];
    const text = (last?.text || "").trim();
    if (!text) return "—";
    return text.length > PREVIEW_MAX ? text.slice(0, PREVIEW_MAX) + "…" : text;
}

/**
 * Flatten messages across every chat — used by the ToneMirror dashboard so
 * insights aggregate across the whole device, not just the active chat.
 */
export function flattenAllMessages(store) {
    const out = [];
    for (const c of store?.chats || []) {
        for (const m of c.messages || []) {
            out.push(m);
        }
    }
    return out;
}
